<template>
    <div class="space-y-6">
        <div
            class="flex flex-col justify-between gap-2 border-b border-t border-gray-200 bg-gray-50 px-2 py-4 md:flex-row md:items-center"
        >
            <h1 class="text-left text-2xl font-medium uppercase tracking-wider">
                {{ calendar.title }}
            </h1>

            <div class="flex flex-row items-end justify-between gap-4">
                <div class="flex flex-col items-start text-base font-normal">
                    <label for="calendar-group">{{
                        t('calendar.category')
                    }}</label>
                    <prime-dropdown
                        id="calendar-group"
                        :model-value="selectedGroup"
                        :options="calendarGroups"
                        option-label="text"
                        option-value="key"
                        :placeholder="t('calendar.all')"
                        @update:model-value="handleUpdateSelectedGroup"
                    />
                </div>

                <div
                    v-if="showAddToCalendar"
                    class="flex flex-col items-start text-base font-normal"
                >
                    <prime-dropdown
                        id="add-to-calendar"
                        v-model="selectedAddToCalendar"
                        :options="addToCalendarOptions"
                        option-label="text"
                        option-value="key"
                        :placeholder="t('calendar.add')"
                    >
                        <template #option="slotProps">
                            <div class="flex items-center gap-2">
                                <span
                                    v-if="slotProps.option.key === 'google'"
                                    class="pi pi-google"
                                />
                                <span
                                    v-else-if="slotProps.option.key === 'apple'"
                                    class="pi pi-apple"
                                />
                                <span
                                    v-else-if="
                                        slotProps.option.key === 'outlook'
                                    "
                                    class="pi pi-microsoft"
                                />
                                <span
                                    v-else-if="slotProps.option.key === 'copy'"
                                    class="pi pi-copy"
                                />
                                <span
                                    v-else-if="slotProps.option.key === 'ics'"
                                    class="pi pi-download"
                                />
                                <div>{{ slotProps.option.text }}</div>
                            </div>
                        </template>
                    </prime-dropdown>
                </div>
            </div>
        </div>

        <div v-html="calendar.body" />

        <div class="relative">
            <full-calendar
                ref="fullCalendarInstance"
                :options="calendarOptions"
            >
                <template #eventContent="arg">
                    <div class="relative mb-2">
                        <div
                            class="fc-event-main-frame whitespace-normal"
                            :class="
                                arg.view.type === 'dayGridMonth'
                                    ? 'md:py-1 md:pl-2'
                                    : 'py-1 pl-2'
                            "
                            :style="{
                                color:
                                    arg.view.type === 'listWeek'
                                        ? arg.event.textColor
                                        : undefined,
                                backgroundColor:
                                    arg.view.type === 'listWeek'
                                        ? arg.event.backgroundColor
                                        : undefined
                            }"
                        >
                            <span
                                class="fc-event-time text-sm !font-light italic tracking-wider"
                                >{{
                                    formatTimeText(
                                        arg.event.start,
                                        arg.event.end
                                    )
                                }}</span
                            ><br /><span class="fc-event-title-container"
                                ><span
                                    class="fc-event-title fc-sticky font-semibold"
                                    >{{ arg.event.title }}</span
                                ></span
                            >
                            <template
                                v-if="
                                    arg.event.extendedProps.instructorText !==
                                    null
                                "
                            >
                                <br /><span class="text-sm">{{
                                    arg.event.extendedProps.instructorText
                                }}</span>
                            </template>
                            <br /><span class="text-sm italic">{{
                                arg.event.extendedProps.capacityText
                            }}</span>
                            <span
                                class="absolute bottom-0 left-0 top-0 w-1 brightness-50"
                                :class="
                                    arg.view.type === 'dayGridMonth'
                                        ? 'hidden md:block'
                                        : ''
                                "
                                :style="`background-color: ${arg.event.extendedProps.backgroundColor}`"
                            />
                        </div>
                    </div>
                </template>
            </full-calendar>

            <div
                v-if="isLoading"
                class="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-full transform"
            >
                <prime-progress-spinner />
            </div>

            <div
                v-show="displayedRef !== undefined"
                ref="floating"
                class="z-10 bg-white p-6 text-sm shadow-lg"
                :class="[
                    hasTransition
                        ? 'transition-[height,width,transform] duration-200'
                        : ''
                ]"
                :style="floatingStyles"
                @mouseenter="handleOverlayMouseEnter"
                @mouseleave="handleOverlayMouseLeave"
            >
                <div
                    v-if="selectedEvent !== undefined"
                    class="space-y-4"
                >
                    <div class="flex items-start justify-between gap-4">
                        <div class="text-xl font-medium text-gray-900">
                            {{ selectedEvent.title }}
                        </div>

                        <prime-button
                            v-if="!isDesktop"
                            text
                            icon="pi pi-times"
                            class="-m-3"
                            @click="handleCloseOverlay"
                        />
                    </div>

                    <div
                        v-if="
                            selectedEvent.extendedProps.instructorText !== null
                        "
                    >
                        <span class="font-medium text-gray-900"
                            >{{ t('calendar.instructors') }}&nbsp;</span
                        >{{ selectedEvent.extendedProps.instructorText }}
                    </div>

                    <div
                        class="max-w-[400px]"
                        v-html="selectedEvent.extendedProps.shortSummary"
                    />

                    <booking-availability-notice
                        customer-note-class="text-xs italic"
                        :has-customer-specific-rules="
                            selectedEvent.extendedProps.bookingPeriod
                                ?.hasCustomerSpecificRules ?? false
                        "
                        :period="
                            selectedEvent.extendedProps.bookingPeriod ??
                            undefined
                        "
                        :explanation="
                            selectedEvent.extendedProps.bookingPeriod
                                ?.explanation
                        "
                    />
                    <div v-if="selectedEvent.extendedProps.buttonText !== null">
                        <nuxt-link
                            v-if="
                                embedParams !== null &&
                                (embedParams.mode === 'same-tab' ||
                                    embedParams.mode === 'lightbox')
                            "
                            custom
                            :to="selectedEvent.extendedProps.link"
                        >
                            <a
                                :href="selectedEvent.extendedProps.link"
                                @click.prevent="
                                    handleClick(
                                        $event,
                                        selectedEvent.extendedProps.link
                                    )
                                "
                            >
                                <prime-button outlined>
                                    {{ selectedEvent.extendedProps.buttonText }}
                                </prime-button>
                            </a>
                        </nuxt-link>

                        <nuxt-link
                            v-else
                            :to="selectedEvent.extendedProps.link"
                            :target="
                                embedParams !== null &&
                                embedParams.mode === 'new-tab'
                                    ? '_blank'
                                    : undefined
                            "
                        >
                            <prime-button outlined>
                                {{ selectedEvent.extendedProps.buttonText }}
                            </prime-button>
                        </nuxt-link>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import PrimeDropdown from 'primevue/dropdown'
import BookingAvailabilityNotice from '~/components/PurchaseContract/Program/BookingAvailabilityNotice.vue'
import { useViewer } from '~/composables/use-login'
import type { StorefrontCalendarQueryQuery } from '~/resources/graphql/graphql'
import PrimeProgressSpinner from 'primevue/progressspinner'
import PrimeButton from 'primevue/button'
import type { StorefrontCalendarScalar } from '~/resources/scalars/storefront-catalog-scalar'
import { useSelectedLanguage } from '~/composables/use-selected-language'
import {
    computed,
    onMounted,
    onUnmounted,
    ref,
    toRaw,
    watch,
    watchEffect
} from 'vue'
import { useActiveFacility } from '~/composables/use-active-facility'
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import listPlugin from '@fullcalendar/list'
import type { Calendar } from '@fullcalendar/core'
import interactionPlugin from '@fullcalendar/interaction'
import { useQuery } from '@tanstack/vue-query'
import { graphql } from '~/resources/graphql'
import { useRpQuery } from '~/composables/graphql'
import { useNuxtApp, useRoute, useState } from '#app'
import {
    now,
    parseLocal,
    toTimelessIso
} from '~/composables/support/localization'
import { addSeconds } from 'date-fns'
import { useRouter } from 'vue-router'
import NuxtLink from '#app/components/nuxt-link'
import {
    layoutModeLightboxLink,
    unsafe_facilityCreateContractLink,
    useRouteLink
} from '~/composables/routing'
import { useEmbedParams } from '~/composables/use-embed-params'
import { getRequestHost } from 'h3'
import type { PlanType } from '~/resources/graphql/graphql'
import {
    offset,
    shift,
    flip,
    useFloating,
    autoPlacement
} from '@floating-ui/vue'
import { useToast } from 'primevue/usetoast'
import { isLocalStorageTestMode, isStaging } from '~/composables/use-org'
import { getIcalendarLink } from '~/composables/iCalendar'
import { useLocalization } from '~/composables/use-localization'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    calendar: StorefrontCalendarScalar
    storefrontPrefix: string
}>()

const isDesktop =
    process.server || window.document.documentElement.clientWidth > 800
const initialView = isDesktop ? 'dayGridMonth' : 'listWeek'

const toolbarViews = isDesktop
    ? 'dayGridMonth,dayGridWeek,dayGridDay'
    : 'dayGridMonth,listWeek,dayGridDay'

const eventTimeFormat = {
    hour: 'numeric',
    minute: '2-digit',
    meridiem: 'short'
}

const useIsStaging = isStaging()
const showAddToCalendar = computed(() => {
    return isLocalStorageTestMode() || import.meta.env.DEV || useIsStaging.value
})

const { startOfWeek } = useLocalization()
const { t } = useI18n({ useScope: 'global' })

const calendarOptions = computed(() => ({
    plugins: [dayGridPlugin, interactionPlugin, listPlugin],
    initialView,
    headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: toolbarViews
    },
    buttonText: {
        list: t('calendar.week')
    },
    height: 'auto',
    nowIndicator: true,
    events: events.value,
    eventTimeFormat,
    displayEventEnd: true,
    dayCellContent: true,
    eventMouseEnter: handleEventEnter,
    eventMouseLeave: handleEventLeave,
    eventDidMount: function (info) {
        // In list view only
        if (info.view.type === 'listWeek') {
            // Get the third child
            if (info.el.children.length === 3) {
                // Make it span the whole width
                info.el.children[2].colSpan = '3'
            }
        }
    },
    firstDay: startOfWeek.value
}))

const activeFacility = useActiveFacility()
const facilityId = computed(() => activeFacility.value.id)

const route = useRoute()
const groupParam = 'group'
const defaultKey = 'all'
const selectedGroup = computed<string>(() => {
    const groups = route.query[groupParam]
    if (groups === undefined || groups === null) {
        return defaultKey
    } else if (typeof groups === 'string') {
        if (groups === '') {
            return defaultKey
        } else {
            return groups
        }
    } else {
        if (groups.length === 0) {
            return defaultKey
        }

        if (groups[0] === null) {
            return defaultKey
        }

        return groups[0]
    }
})

const router = useRouter()
function handleUpdateSelectedGroup(newGroup: string) {
    if (newGroup === selectedGroup.value) {
        return
    }

    router.push({
        ...route,
        query: {
            ...route.query,
            [groupParam]: newGroup
        }
    })
}

const calendarGroups = computed(() => {
    return [
        { key: defaultKey, text: t('calendar.all') },
        ...props.calendar.children.map(group => ({
            key: group.slug,
            text: group.title
        }))
    ]
})

const selectedAddToCalendar = ref('')
watch(selectedAddToCalendar, newValue => {
    if (newValue === 'google') {
        window.open(
            'https://calendar.google.com/calendar/u/0/r?cid=https://' +
                iCalLink.value +
                '&target=google',
            '_blank'
        )
    } else if (newValue === 'apple') {
        window.open('webcal://' + iCalLink.value + '&target=apple', '_blank')
    } else if (newValue === 'outlook') {
        window.open('webcal://' + iCalLink.value + '&target=outlook', '_blank')
    } else if (newValue === 'copy') {
        copyIcalLink()
    } else if (newValue === 'ics') {
        window.open('https://' + iCalLink.value + '&target=ics', '_blank')
    }
    selectedAddToCalendar.value = ''
})

const addToCalendarOptions = computed(() => [
    { key: 'google', text: t('calendar.google') },
    { key: 'apple', text: t('calendar.apple') },
    { key: 'outlook', text: t('calendar.outlook') },
    { key: 'copy', text: t('calendar.copyImportUrl') },
    { key: 'ics', text: t('calendar.downloadIcs') }
])

const storefrontBaseSlug = useState('storefrontParamSlug')
const storefrontSlug = computed(() => {
    return typeof storefrontBaseSlug.value === 'string' &&
        storefrontBaseSlug.value.startsWith('kiosk-')
        ? storefrontBaseSlug.value.substring(6)
        : storefrontBaseSlug.value
})

const iCalLink = computed(() => {
    return getIcalendarLink(
        activeFacility.value.organization.code,
        encodeURIComponent(activeFacility.value.id),
        props.calendar.guid,
        storefrontSlug.value
    )
})

const toast = useToast()
function copyIcalLink() {
    navigator.clipboard.writeText(iCalLink.value).then(
        function () {
            toast.add({
                severity: 'success',
                summary: t('calendar.linkCopied'),
                life: 5000,
                closable: false
            })
        },
        function () {
            toast.add({
                severity: 'error',
                summary: t('calendar.unableToCopy'),
                life: 5000,
                closable: false
            })
        }
    )
}

const embedParams = useEmbedParams()
function makeContractLink(planType: PlanType, slug: string): string {
    if (embedParams?.mode === 'lightbox') {
        return layoutModeLightboxLink(
            unsafe_facilityCreateContractLink(
                props.storefrontPrefix,
                planType,
                slug
            )
        )
    } else {
        return facilityCreateContractLink(
            props.storefrontPrefix,
            planType,
            slug
        )
    }
}

type PlanInfo = {
    contractLink: string
    planType: PlanType
}

const { facilityCreateContractLink } = useRouteLink()
const planIdToInfoMap = computed<Map<string, PlanInfo>>(() => {
    if (selectedGroup.value === defaultKey) {
        return new Map(
            props.calendar.children.flatMap(group => {
                return group.children.map(child => [
                    child.planId,
                    {
                        contractLink: makeContractLink(
                            child.planType,
                            child.slug
                        ),
                        planType: child.planType
                    }
                ])
            })
        )
    }

    return new Map(
        props.calendar.children.flatMap(group => {
            if (selectedGroup.value === group.slug) {
                return group.children.map(child => [
                    child.planId,
                    {
                        contractLink: makeContractLink(
                            child.planType,
                            child.slug
                        ),
                        planType: child.planType
                    }
                ])
            }

            return []
        })
    )
})

const planIds = computed(() => Array.from(planIdToInfoMap.value.keys()))
const rpQuery = useRpQuery()
const selectedLanguage = useSelectedLanguage()
const viewer = useViewer()
const viewerCustomerId = computed(() => viewer.value?.customerId ?? null)

const startDate = ref<string | undefined>()
const endDate = ref<string | undefined>()

const { data, isLoading } = useQuery({
    queryKey: [
        'StorefrontCalendarQuery',
        facilityId,
        planIds,
        startDate,
        endDate,
        selectedLanguage,
        viewerCustomerId
    ],
    queryFn: () => {
        if (startDate.value === undefined || endDate.value === undefined) {
            return null
        }

        return rpQuery(
            graphql(/* GraphQL */ `
                query StorefrontCalendarQuery(
                    $input: CalendarFilter
                    $language: Language!
                    $bookingPeriodFilter: BookingPeriodFilter!
                ) {
                    calendar(input: $input) {
                        courseId
                        sessionGraphId
                        sessionFacilityHash
                        startLocal
                        endLocal
                        sessionSequence
                        sessionCount
                        textColor
                        backgroundColor
                        publicTitle
                        capacityText
                        bookingPeriod(filter: $bookingPeriodFilter) {
                            status
                            opensAt
                            displayDate
                            hasCustomerSpecificRules
                            explanation(language: $language)
                        }
                        instructorText
                        buttonText
                        planId
                        shortSummary(language: $language)
                    }
                }
            `),
            {
                input: {
                    facilityId: [facilityId.value],
                    planId: planIds.value,
                    startDate: startDate.value,
                    endDate: endDate.value
                },
                language: selectedLanguage.value,
                bookingPeriodFilter: { customerId: viewerCustomerId.value }
            }
        )
    }
})

watch(viewerCustomerId, () => handleCloseOverlay())

type CalendarEvent = {
    id: string
    start: Date
    end: Date
    title: string
    display: 'block'
    textColor: string
    backgroundColor: string
    extendedProps: {
        planId: string
        capacityText: string
        bookingPeriod: StorefrontCalendarQueryQuery['calendar'][number]['bookingPeriod']
        instructorText: string
        buttonText: string
        shortSummary: string
        link: string
        backgroundColor: string
    }
}

const events = computed(() => {
    if (data.value === undefined) {
        return []
    }

    if (data.value === null) {
        return []
    }

    return data.value.calendar.map(sessionEvent => {
        let title = sessionEvent.publicTitle
        if (sessionEvent.sessionCount > 1) {
            // TODO: Localize
            title =
                sessionEvent.publicTitle +
                ' ' +
                sessionEvent.sessionSequence +
                '/' +
                sessionEvent.sessionCount
        }

        const planInfo = planIdToInfoMap.value.get(sessionEvent.planId)

        if (planInfo === undefined) {
            throw new Error()
        }

        let link = planInfo.contractLink
        if (planInfo.planType === 'PROGRAM') {
            link +=
                '?date=' +
                toTimelessIso(
                    parseLocal(
                        sessionEvent.bookingPeriod?.displayDate ??
                            sessionEvent.startLocal
                    )
                ) +
                '&course=' +
                sessionEvent.courseId +
                '&session=' +
                sessionEvent.sessionGraphId
        }

        return {
            id: sessionEvent.sessionFacilityHash,
            start: parseLocal(sessionEvent.startLocal),
            end: addSeconds(parseLocal(sessionEvent.endLocal), 1),
            title,
            display: 'block',
            textColor: sessionEvent.textColor,
            backgroundColor: sessionEvent.backgroundColor,
            extendedProps: {
                planId: sessionEvent.planId,
                capacityText: sessionEvent.capacityText,
                bookingPeriod: sessionEvent.bookingPeriod ?? null,
                instructorText: sessionEvent.instructorText,
                buttonText: sessionEvent.buttonText,
                // Keep the overlay content attached to the calendar event so the
                // storefront calendar does not need a separate plan lookup.
                shortSummary: sessionEvent.shortSummary ?? '',
                link: link,
                backgroundColor: sessionEvent.backgroundColor
            }
        }
    })
})

function handleDatesSet(dateInfo: { start: Date; end: Date }) {
    const newStartDate = dateInfo.start
    const newEndDate = dateInfo.end
    const nowDate = now(activeFacility.value.timezone)

    if (newStartDate > nowDate) {
        startDate.value = toTimelessIso(newStartDate)
    } else {
        startDate.value = toTimelessIso(nowDate)
    }

    if (newEndDate > nowDate) {
        endDate.value = toTimelessIso(newEndDate)
    } else {
        endDate.value = toTimelessIso(nowDate)
    }
}

const fullCalendarInstance = ref<InstanceType<typeof FullCalendar>>()
let fullCalendarApi: Calendar | undefined = undefined
onMounted(() => {
    if (fullCalendarInstance.value !== undefined) {
        fullCalendarApi = fullCalendarInstance.value.getApi()
        fullCalendarApi.on('datesSet', handleDatesSet)
        handleDatesSet({
            start: fullCalendarApi.getCurrentData().dateProfile.renderRange
                .start,
            end: fullCalendarApi.getCurrentData().dateProfile.renderRange.end
        })
    }
})

onUnmounted(() => {
    fullCalendarApi?.off('datesSet', handleDatesSet)
})

function formatTimeText(start, end) {
    return fullCalendarApi?.formatRange(
        toRaw(start),
        toRaw(end),
        eventTimeFormat
    )
}

// Currently showing overlay above this node
const displayedRef = ref<{ node: CalendarEvent; element: HTMLDivElement }>()
const selectedEvent = computed(() => displayedRef.value?.node)
// Mouse is inside the overlay, so keep showing overlay
const mouseInOverlay = ref(false)
// We are no longer over an event, so when the mouse leaves the overlay, hide
// the overlay
const hideOnLeave = ref(false)

function handleEventEnter(e) {
    // If mouse is in the overlay, do not change event
    if (mouseInOverlay.value) {
        return
    }

    // Reset hide on leave, because we have a new event we are showing
    hideOnLeave.value = false

    // If we are not showing an event, show overlay immediately
    if (selectedEvent.value === undefined) {
        // Show the new event
        displayedRef.value = {
            node: e.event,
            element: e.el
        }
    }
    // Otherwise, wait 100ms to see if the mouse is in the overlay - if it is
    // then do not show the new event
    else {
        setTimeout(() => {
            // If mouse is in the overlay, do not change event
            if (mouseInOverlay.value) {
                return
            }

            // Show the new event
            displayedRef.value = {
                node: e.event,
                element: e.el
            }
        }, 100)
        return
    }
}

function handleEventLeave(e) {
    // Hide after 100ms if we are not hovering over a new element
    setTimeout(() => {
        // Check if the selected event has not changed
        if (e.el === displayedRef.value?.element) {
            // If the mouse is in the overlay, mark that when it leaves we can
            // hide the overlay - the handleEventEnter will reset this to false
            // if we mouse over a new event to show
            if (mouseInOverlay.value) {
                hideOnLeave.value = true
                return
            }
            // Otherwise, time to hide the overlay
            else {
                displayedRef.value = undefined
            }
        }
    }, 250)
}

function handleOverlayMouseEnter() {
    mouseInOverlay.value = true
}

function handleOverlayMouseLeave() {
    mouseInOverlay.value = false
    // When the mouse leaves the overlay, if we need to, hide the overlay
    if (hideOnLeave.value) {
        displayedRef.value = undefined
    }
}

function handleCloseOverlay() {
    // Reset everything
    mouseInOverlay.value = false
    hideOnLeave.value = false
    displayedRef.value = undefined
}

const hasTransition = ref(false)
watch(displayedRef, () => {
    setTimeout(() => {
        if (displayedRef.value !== undefined) {
            hasTransition.value = true
        } else {
            hasTransition.value = false
        }
    }, 30)
})

const reference = computed(() => displayedRef.value?.element ?? null)
const floating = ref(null)
const { floatingStyles } = useFloating(
    reference,
    floating,
    isDesktop
        ? {
              placement: 'left-start',
              middleware: [
                  offset(({ rects }) => {
                      return {
                          mainAxis: -10,
                          crossAxis: rects.reference.height - 15
                      }
                  }),
                  shift(),
                  flip()
              ]
          }
        : {
              placement: 'top',
              middleware: [autoPlacement()]
          }
)

const menuOverlayStyles = ref<Record<string, string | number>>()
watchEffect(() => {
    if (displayedRef.value !== undefined) {
        const rect = displayedRef.value.element.getBoundingClientRect()
        menuOverlayStyles.value = {
            top: rect.bottom - 7 + 'px',
            transform: 'translateX(' + rect.left + 'px)'
        }
    }
})

const nuxt = useNuxtApp()
const locationOrigin = computed(() => {
    if (process.server && nuxt.ssrContext) {
        return getRequestHost(nuxt.ssrContext.event)
    } else if (location !== undefined) {
        return location.origin
    } else {
        return ''
    }
})

function handleClick(event: PointerEvent, url: string) {
    if (embedParams?.mode === 'same-tab') {
        parent.postMessage(
            'RPHQ::1::' +
                JSON.stringify({
                    key: 'parent-redirect',
                    url: locationOrigin.value + url
                }),
            '*'
        )
    } else if (embedParams?.mode === 'lightbox') {
        parent.postMessage(
            'RPHQ::1::' +
                JSON.stringify({
                    key: 'lightbox',
                    url: locationOrigin.value + url,
                    backgroundColor: activeFacility.value.branding.primaryColor
                }),
            '*'
        )
    } else {
        throw new Error()
    }
}
</script>

<style lang="postcss">
.fc-event-main-frame {
    display: block !important;
}

.fc-daygrid-block-event {
    @apply rounded-none border-0;
}

.fc-daygrid-day-events {
    @apply !min-h-32;
}

.fc-day-past.fc-daygrid-day {
    @apply bg-slate-100;
}

.fc-list-event-time,
.fc-list-event-graphic {
    display: none;
}

.fc .fc-list-table td {
    @apply text-sm;
    padding: 1px 4px 7px;
}

.fc .fc-list-table .fc-list-day {
    @apply children:border-b-0;
}

.fc .fc-list-table .fc-list-day-cushion {
    @apply mb-4 border-b;
}

.fc .fc-list-table .fc-list-event-title {
    @apply border-0;
}

@media (max-width: 640px) {
    .fc-toolbar.fc-header-toolbar {
        display: flex;
        flex-wrap: wrap;
    }
    .fc-toolbar.fc-header-toolbar .fc-toolbar-chunk:nth-child(1) {
        order: 3;
        flex-basis: 100%;
        display: flex;
        justify-content: space-between;
        padding-top: 1rem;
    }
    .fc-toolbar.fc-header-toolbar .fc-toolbar-chunk:nth-child(2) {
        order: 1;
    }
    .fc-toolbar.fc-header-toolbar .fc-toolbar-chunk:nth-child(3) {
        order: 2;
    }
}
</style>
